import * as Immutable from "immutable";
import { selectWidgetPositions } from "views/logic/slices/widgetSelectors";
import {
  selectActiveQuery,
  selectActiveViewState,
  selectWidget,
  selectWidgets
} from "views/logic/slices/viewSelectors";
import { updateViewState } from "views/logic/slices/viewSlice";
import generateId from "logic/generateId";
import { setTitle } from "views/logic/slices/titlesActions";
import WidgetFormattingSettings from "views/logic/aggregationbuilder/WidgetFormattingSettings";
import GenerateNextPosition from "views/logic/views/GenerateNextPosition";
import normalizeViewState from "views/logic/views/normalizeViewState";
import { setNewWidget } from "views/logic/slices/widgetsSlice";
export const updateWidgetPositions = (newWidgetPositions) => (dispatch, getState) => {
  const activeQuery = selectActiveQuery(getState());
  const activeViewState = selectActiveViewState(getState());
  const newViewState = activeViewState.toBuilder().widgetPositions(newWidgetPositions).build();
  return dispatch(updateViewState(activeQuery, newViewState));
};
export const updateWidgetPosition = (id, newWidgetPosition) => (dispatch, getState) => {
  const widgetPositions = selectWidgetPositions(getState());
  const newWidgetPositions = { ...widgetPositions, [id]: newWidgetPosition };
  return dispatch(updateWidgetPositions(newWidgetPositions));
};
export const updateWidgets = (newWidgets) => (dispatch, getState) => {
  const activeQuery = selectActiveQuery(getState());
  const activeViewState = selectActiveViewState(getState());
  const newViewState = activeViewState.toBuilder().widgets(newWidgets).build();
  const newViewStateNormalized = normalizeViewState(newViewState);
  return dispatch(updateViewState(activeQuery, newViewStateNormalized));
};
export const addWidget = (widget, position) => async (dispatch, getState) => {
  if (widget.id === void 0) {
    throw new Error("Unable to add widget without id to query.");
  }
  const widgets = selectWidgets(getState());
  const widgetPositions = Immutable.Map(selectWidgetPositions(getState()));
  const newWidgets = widgets.push(widget);
  const newWidgetPositions = position ? widgetPositions.set(widget.id, position) : GenerateNextPosition(widgetPositions, newWidgets.toArray());
  const activeQuery = selectActiveQuery(getState());
  const activeViewState = selectActiveViewState(getState());
  const newViewState = activeViewState.toBuilder().widgetPositions(newWidgetPositions).widgets(newWidgets).build();
  const result = dispatch(updateViewState(activeQuery, newViewState)).then(() => widget.id);
  dispatch(setNewWidget(widget.id));
  return result;
};
export const updateWidget = (id, updatedWidget) => (dispatch, getState) => {
  const widgets = selectWidgets(getState());
  const newWidgets = widgets.map((widget) => widget.id === id ? updatedWidget : widget).toList();
  return dispatch(updateWidgets(newWidgets));
};
export const updateWidgetConfig = (id, newWidgetConfig) => (dispatch, getState) => {
  const widget = selectWidget(id)(getState());
  const newWidget = widget.toBuilder().config(newWidgetConfig).build();
  return dispatch(updateWidget(id, newWidget));
};
export const duplicateWidget = (widgetId, widgetTitle) => async (dispatch, getState) => {
  const widget = selectWidget(widgetId)(getState());
  if (!widget) {
    throw new Error(`Unable to duplicate widget with id "${widgetId}", it is not found.`);
  }
  const activeQuery = selectActiveQuery(getState());
  const duplicatedWidget = widget.duplicate(generateId());
  return dispatch(addWidget(duplicatedWidget)).then(
    () => dispatch(setTitle(activeQuery, "widget", duplicatedWidget.id, `${widgetTitle} (copy)`))
  );
};
export const removeWidget = (widgetId) => async (dispatch, getState) => {
  const widgets = selectWidgets(getState());
  const newWidgets = widgets.filter((widget) => widget.id !== widgetId).toList();
  return dispatch(updateWidgets(newWidgets));
};
export const setChartColor = (widgetId, name, color) => (dispatch, getState) => {
  var _a, _b;
  const widget = selectWidget(widgetId)(getState());
  const formattingSettings = (_b = (_a = widget == null ? void 0 : widget.config) == null ? void 0 : _a.formattingSettings) != null ? _b : WidgetFormattingSettings.empty();
  const { chartColors } = formattingSettings;
  const newWidget = widget.toBuilder().config(
    widget.config.toBuilder().formattingSettings(
      formattingSettings.toBuilder().chartColors({ ...chartColors, [name]: color }).build()
    ).build()
  ).build();
  return dispatch(updateWidget(widgetId, newWidget));
};
export const setWidgetTitle = (widgetId, newTitle) => async (dispatch, getState) => {
  const activeQuery = selectActiveQuery(getState());
  return dispatch(setTitle(activeQuery, "widget", widgetId, newTitle));
};
export const updateDescription = (widget, newDescription) => async (dispatch) => {
  const updatedWidget = widget.withDescription(newDescription);
  return dispatch(updateWidget(widget.id, updatedWidget));
};
